import * as React from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams, type RouteObject } from 'react-router-dom'
import { isSitePath } from '@lib/helpers/site-paths'
import { ProtectedRoute } from '@/auth/ProtectedRoute'
import { RequireAccess } from '@/auth/RequireAccess'
import { ANY_STAFF, canCreateEvents, homePathFor, type AccessRule } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { useAuth } from '@/auth/AuthContext'
import { AppLayout } from '@/components/AppLayout'
import { ContinueToSite } from '@/components/ContinueToSite'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { LoginPage } from '@/pages/LoginPage'
import { AcceptInvitePage } from '@/pages/AcceptInvitePage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { MagicLinkPage } from '@/pages/MagicLinkPage'
import { ConnectPage } from '@/pages/ConnectPage'
import { ConnectProblemPage } from '@/pages/ConnectProblemPage'
import { BillingPage } from '@/pages/BillingPage'
import { SignupPage } from '@/pages/SignupPage'
import { LegalDocumentPage } from '@/pages/LegalDocumentPage'
import { UsersPage } from '@/pages/UsersPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { VenuesPage } from '@/pages/VenuesPage'
import { VenueDetailPage } from '@/pages/VenueDetailPage'
import { FurnitureItemPage } from '@/pages/FurnitureItemPage'
import { EventsPage } from '@/pages/EventsPage'
import { CreateEventPage } from '@/pages/CreateEventPage'
import { EventDetailPage } from '@/pages/EventDetailPage'
import { CoupleFeedbackPage } from '@/pages/CoupleFeedbackPage'
import { FeedbackQueuePage } from '@/pages/FeedbackQueuePage'
import { FeedbackDetailPage } from '@/pages/FeedbackDetailPage'
import { BookingPage } from '@/pages/BookingPage'
import { VendorDirectoryPage } from '@/pages/VendorDirectoryPage'
import { VendorRequestsPage } from '@/pages/VendorRequestsPage'
import { VendorReviewsPage } from '@/pages/VendorReviewsPage'
import { VenueSpaceSettingsPage } from '@/pages/VenueSpaceSettingsPage'
import { TenantsPage } from '@/pages/TenantsPage'
import { TenantFormPage } from '@/pages/TenantFormPage'
import { SuperAdminsPage } from '@/pages/SuperAdminsPage'
import { VendorsPage } from '@/pages/VendorsPage'
import { ReviewsToApprovePage } from '@/pages/ReviewsToApprovePage'
import { VendorAdminPage } from '@/pages/VendorAdminPage'
import { VendorPage } from '@/pages/VendorPage'
import { VendorProfilePage } from '@/pages/VendorProfilePage'
import { VendorVenuesPage } from '@/pages/VendorVenuesPage'
import { VendorPeoplePage } from '@/pages/VendorPeoplePage'
import { VendorEventsPage } from '@/pages/VendorEventsPage'
import { VendorEventPage } from '@/pages/VendorEventPage'
import { VendorFurniturePage } from '@/pages/VendorFurniturePage'
import { PrismImportPage } from '@/pages/PrismImportPage'
import { GoHighLevelPage } from '@/pages/GoHighLevelPage'
import { AdsPage } from '@/pages/AdsPage'
import { AdsGoogleReturnPage } from '@/pages/AdsGoogleReturnPage'
import { HoneyBookReturnPage } from '@/pages/HoneyBookReturnPage'
import { VenueAdsPage } from '@/pages/VenueAdsPage'
import { VenueDashboardPage } from '@/pages/VenueDashboardPage'
import { VenueCalendarPage } from '@/pages/VenueCalendarPage'
import { LeadsPage } from '@/pages/LeadsPage'
import { PipelinePage } from '@/pages/PipelinePage'
import { GettingPaidPage } from '@/pages/GettingPaidPage'
import { VenuePipelinePage } from '@/pages/VenuePipelinePage'
import { RolesPage } from '@/pages/RolesPage'
import { RoleEditorPage } from '@/pages/RoleEditorPage'
import { PublicSchedulePage } from '@/pages/public/PublicSchedulePage'
import { SignupVendorPage } from '@/pages/SignupVendorPage'
import { ConfirmEmailPage } from '@/pages/ConfirmEmailPage'
import { PayPage } from '@/pages/public/PayPage'
import { PublicEventPage } from '@/pages/public/PublicEventPage'
import { PRODUCT_NAME } from '@lib/constants/product'

/** Send each session to its home — see `homePathFor`. Every page guard redirects
 * here, so a misrouted user lands on the right home instead of bouncing between
 * guards. */
function HomeRedirect() {
  const { user } = useAuth()
  return <Navigate to={homePathFor(user)} replace />
}

/** Furniture used to be a page of its own. It is a section of the venue's
 * settings now, beside the inventory counts it is read against, so the old path
 * follows it there rather than 404ing on somebody's bookmark. Outside a venue
 * there is no catalog to show, and home is the honest answer. */
/**
 * The new-event form, for someone working at a venue (`canCreateEvents`). An
 * organization admin across the whole organization logs in to the venue first,
 * so is sent back to the events list.
 */
function CreateEventRoute() {
  const { user } = useAuth()
  return canCreateEvents(user) ? <CreateEventPage /> : <Navigate to="/events" replace />
}

/** Where this page was loaded, before any move inside the app. */
const LOADED_AT = window.location.pathname

/**
 * The app and the public site share the domain (docs/public-site.spec.md), and
 * a full page load of a site address never reaches the app. Two things still can:
 * a move inside the app to a site address, and a site page with a trailing slash
 * (`/venues/<name>/`), which the front door leaves to the app. Either is loaded
 * again from the site. `/` stays: inside the app it is the way home.
 */
function SiteHandoff() {
  const { pathname, search, hash } = useLocation()
  const trimmed = pathname.replace(/\/+$/, '')
  // Never to the address the page was loaded at: that load already went past the
  // front door, so asking again would only reload this (as in local development,
  // where there is no front door) for ever.
  const theSites = trimmed !== '' && trimmed !== LOADED_AT && isSitePath(trimmed)
  React.useEffect(() => {
    if (theSites) window.location.replace(`${trimmed}${search}${hash}`)
  }, [theSites, trimmed, search, hash])
  return theSites ? null : <Outlet />
}

/** A platform admin's vendor page, from before it moved under `/admin`. */
function VendorAdminVendorRedirect() {
  const { vendorId } = useParams()
  return <Navigate to={`/admin/vendors/${vendorId ?? ''}`} replace />
}

function VenueGoHighLevelRedirect() {
  const { venueId } = useParams()
  return <Navigate to={`/venues/${venueId ?? ''}/settings#gohighlevel`} replace />
}

function FurnitureRedirect() {
  const { user } = useAuth()
  if (isInVenueSupportMode(user)) {
    return <Navigate to={`/venues/${user.supportVenue.venueId}/settings#furniture`} replace />
  }
  return <Navigate to={homePathFor(user)} replace />
}

/** The event's own people: its staff, its couple, and its planner. The API narrows each to their own events. */
const EVENT_PEOPLE: AccessRule = { staff: 'events:view', client: true, vendor: true }

/**
 * Application routes. `/login` is public; everything else is nested under
 * `ProtectedRoute` and the `AppLayout` chrome. A platform SUPER_ADMIN sees
 * `/tenants` and `/admin/vendors`; a VENDOR user sees only `/vendor`; a company's
 * clients see their events, and its staff what their role allows
 * (docs/custom-roles.spec.md), each page held to the rule the API holds the
 * routes it reads to. `/` redirects to the session's home, and every guard
 * bounces there.
 */
export const appRoutes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  // Public onboarding & recovery (reached from emailed links).
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  { path: '/signup', element: <SignupPage /> },
  // A vendor company signing itself up, and confirming its email (docs/public-site.spec.md).
  { path: '/signup/vendor', element: <SignupVendorPage /> },
  { path: '/confirm-email', element: <ConfirmEmailPage /> },
  { path: '/terms', element: <LegalDocumentPage document="terms" /> },
  { path: '/privacy', element: <LegalDocumentPage document="privacy" /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // Opened from an emailed sign-in link. Public, and deliberately a page rather
  // than a route that spends the token on load — see `MagicLinkPage`.
  { path: '/magic-link', element: <MagicLinkPage /> },
  // Where `/oauth/authorize` sends a request it will not even redirect — an
  // unknown application, or one asking to be returned somewhere it never
  // registered. Public, because nobody reaching it has signed in.
  { path: '/connect/problem', element: <ConnectProblemPage /> },
  // The venue share link. Unauthenticated and outside `AppLayout` on purpose:
  // the reader is a setup crew who will never have an account, and nav, a venue
  // switcher and a sign-in box would all be furniture for somebody else. The
  // token in the path is the whole of the authority — see
  // `docs/public-schedule.md`.
  { path: '/s/:token', element: <PublicSchedulePage /> },
  { path: '/s/:token/:eventId', element: <PublicEventPage /> },
  // A payment's link: whoever has it can pay, a parent with no account included
  // (docs/sales.spec.md, "Paying"). The token is its whole authority.
  { path: '/pay/:token', element: <PayPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      // Approving a connection: signed in, because it names the account it will
      // act as, and outside `AppLayout` because there is one decision here and
      // a venue switcher beside it would be furniture for somebody else.
      { path: 'connect', element: <ConnectPage /> },
      // Back to a page on the public site once signed in: "Ask to serve this
      // venue" on a venue's page comes here when the vendor isn't signed in.
      { path: 'continue', element: <ContinueToSite /> },
      // Full-screen editors: the event's own people, its planner included.
      {
        element: <RequireAccess rule={EVENT_PEOPLE} />,
        // The drawing editors load on their own, the first time one is opened.
        // They and the canvas library were half the one file every page waited
        // for, sign-in included: about 20 seconds of white screen on a slow phone.
        children: [{ path: 'events/:eventId/plan', lazy: async () => ({ Component: (await import('@/pages/EventEditorPage')).EventEditorPage }) }],
      },
      // A room's drawing and its saved layouts. The drawing tools have no
      // read-only mode, so they are for staff who can change spaces; with View,
      // a room is seen from its settings page.
      {
        element: <RequireAccess rule={{ staff: 'spaces:edit' }} />,
        children: [
          {
            path: 'venues/:venueId/spaces/:spaceId',
            lazy: async () => ({ Component: (await import('@/pages/VenueSpaceEditorPage')).VenueSpaceEditorPage }),
          },
          {
            path: 'venues/:venueId/spaces/:spaceId/layouts',
            lazy: async () => ({ Component: (await import('@/pages/VenueLayoutEditorPage')).VenueLayoutEditorPage }),
          },
        ],
      },
      {
        element: <AppLayout />,
        children: [
          // A client-side move to `/` (the app's own fallbacks) still lands at home.
          // Opened in a browser, `/` is the public site's home now, so links into
          // the app's home use `/app`, which the site's "Open the app" uses too.
          { index: true, element: <HomeRedirect /> },
          { path: 'app', element: <HomeRedirect /> },
          // Every signed-in user can manage their own profile.
          { path: 'profile', element: <ProfilePage /> },
          // Platform tenant + vendor + super-admin management — super admin only.
          {
            element: <RequireAccess rule={{ platform: true }} />,
            children: [
              { path: 'tenants', element: <TenantsPage /> },
              { path: 'tenants/new', element: <TenantFormPage /> },
              { path: 'tenants/:tenantId', element: <TenantFormPage /> },
              // Under /admin since the public site took /vendors (docs/public-site.spec.md).
              { path: 'admin/vendors', element: <VendorsPage /> },
              { path: 'admin/vendors/new', element: <VendorAdminPage /> },
              { path: 'admin/vendors/:vendorId', element: <VendorAdminPage /> },
              { path: 'admin/reviews', element: <ReviewsToApprovePage /> },
              // Where they were, for links already out there: the site's front door
              // leaves an id and the word "new" to the app.
              { path: 'vendors/new', element: <Navigate to="/admin/vendors/new" replace /> },
              { path: 'vendors/:vendorId', element: <VendorAdminVendorRedirect /> },
              { path: 'super-admins', element: <SuperAdminsPage /> },
            ],
          },
          // A vendor user's single vendor and its furniture library.
          {
            element: <RequireAccess rule={{ vendor: true }} />,
            children: [
              { path: 'vendor', element: <VendorPage /> },
              { path: 'vendor/profile', element: <VendorProfilePage /> },
              { path: 'vendor/venues', element: <VendorVenuesPage /> },
              { path: 'vendor/people', element: <VendorPeoplePage /> },
              { path: 'vendor/events', element: <VendorEventsPage /> },
              { path: 'vendor/events/:eventId', element: <VendorEventPage /> },
              { path: 'vendor/requests', element: <VendorRequestsPage /> },
              { path: 'vendor/reviews', element: <VendorReviewsPage /> },
              { path: 'vendor/furniture', element: <VendorFurniturePage /> },
            ],
          },
          // Events: staff who see them, and the couple's own. A vendor's list is
          // its own (`/vendor/events`).
          {
            element: <RequireAccess rule={{ staff: 'events:view', client: true }} />,
            children: [{ path: 'events', element: <EventsPage /> }],
          },
          // One event's own page: its people, planner included.
          {
            element: <RequireAccess rule={EVENT_PEOPLE} />,
            children: [{ path: 'events/:eventId', element: <EventDetailPage /> }],
          },
          // The couple's proposal, and their planner's view of it (docs/sales.spec.md).
          {
            element: <RequireAccess rule={{ client: true, vendor: true }} />,
            children: [
              { path: 'events/:eventId/booking', element: <BookingPage /> },
              // The venue's vendors, browsed from the event (docs/vendor-growth.spec.md).
              { path: 'events/:eventId/vendors', element: <VendorDirectoryPage /> },
            ],
          },
          {
            element: <RequireAccess rule={{ client: true }} />,
            children: [{ path: 'events/:eventId/feedback', element: <CoupleFeedbackPage /> }],
          },
          {
            element: <RequireAccess rule={{ staff: 'events:edit' }} />,
            children: [
              { path: 'events/new', element: <CreateEventRoute /> },
              { path: 'feedback', element: <FeedbackQueuePage /> },
              { path: 'feedback/:feedbackId', element: <FeedbackDetailPage /> },
            ],
          },
          {
            element: <RequireAccess rule={{ staff: 'events:view' }} />,
            children: [{ path: 'venues/:venueId/calendar', element: <VenueCalendarPage /> }],
          },
          // A venue's own pages, for any of its staff: each section shows what
          // their role sees, read-only where it can't change it. The API scopes
          // every read and write to the venues they reach.
          {
            element: <RequireAccess rule={{ staff: ANY_STAFF }} />,
            children: [
              // A venue's own page is its dashboard: how it is doing, and what
              // still needs doing. Its calendar and its settings — spaces,
              // tracks, inventory, vendors — are pages beneath it; settings is
              // also where old `#section` deep links are sent (see
              // VenueDashboardPage).
              { path: 'venues/:venueId', element: <VenueDashboardPage /> },
              { path: 'venues/:venueId/settings', element: <VenueDetailPage /> },
              { path: 'furniture', element: <FurnitureRedirect /> },
            ],
          },
          // A room's settings sit in the app shell beside the venue's, not with
          // the full-screen editors above: it is a settings page, and one with
          // no way back to anything is a trap.
          {
            element: <RequireAccess rule={{ staff: 'spaces:view' }} />,
            children: [{ path: 'venues/:venueId/spaces/:spaceId/settings', element: <VenueSpaceSettingsPage /> }],
          },
          // The item editor is reached from the venue's Furniture section, and
          // sits outside it because the shape designer needs the whole width.
          // Opening a piece needs Furniture View, and it reads as read-only
          // without Edit; adding one needs Edit.
          {
            element: <RequireAccess rule={{ staff: 'furniture:edit' }} />,
            children: [{ path: 'furniture/items/new', element: <FurnitureItemPage /> }],
          },
          {
            element: <RequireAccess rule={{ staff: 'furniture:view' }} />,
            children: [{ path: 'furniture/items/:itemId', element: <FurnitureItemPage /> }],
          },
          // The company's own pages, for staff whose role spans it. Someone on
          // assigned venues works in one venue at a time, so a table of every
          // venue is what the single-venue layout leaves out.
          {
            element: <RequireAccess rule={{ staff: ANY_STAFF, companyWide: true }} />,
            // `/venues/all`, since `/venues` is the public site's list of venues.
            children: [{ path: 'venues/all', element: <VenuesPage /> }],
          },
          {
            element: <RequireAccess rule={{ staff: 'people:view', companyWide: true }} />,
            children: [{ path: 'users', element: <UsersPage /> }],
          },
          // Every venue's lead stats (docs/gohighlevel-stats.spec.md, "The company table").
          {
            element: <RequireAccess rule={{ staff: 'leadStats:view', companyWide: true }} />,
            children: [{ path: 'leads', element: <LeadsPage /> }],
          },
          // The company's pipeline, and each venue's (docs/pipeline-view.spec.md). Every company has them.
          {
            element: <RequireAccess rule={{ staff: 'leadStats:view', companyWide: true }} />,
            children: [{ path: 'pipeline', element: <PipelinePage /> }],
          },
          {
            element: <RequireAccess rule={{ staff: 'leadStats:view' }} />,
            children: [{ path: 'venues/:venueId/pipeline', element: <VenuePipelinePage /> }],
          },
          // How the company gets paid for its venues' sales (docs/sales.spec.md, "Stripe").
          {
            element: <RequireAccess rule={{ staff: 'integrations:edit', companyWide: true }} />,
            children: [{ path: 'sales/business', element: <GettingPaidPage /> }],
          },
          // The company's ads (docs/ad-analytics.spec.md); its setup is on the same page for those who set it up.
          { element: <RequireAccess rule={{ staff: 'ads:view', companyWide: true }} />, children: [{ path: 'ads', element: <AdsPage /> }] },
          // A venue's ad numbers.
          {
            element: <RequireAccess rule={{ staff: 'ads:view' }} />,
            children: [{ path: 'venues/:venueId/ads', element: <VenueAdsPage /> }],
          },
          // The company's GoHighLevel settings and Google Ads sign-ins, and where
          // Google's sign-in screen sends the person back to: they apply at every
          // venue, so they are for a Company-wide role.
          {
            element: <RequireAccess rule={{ staff: 'integrations:edit', companyWide: true }} />,
            children: [
              { path: 'gohighlevel', element: <GoHighLevelPage /> },
              { path: 'ads/google/return', element: <AdsGoogleReturnPage /> },
            ],
          },
          {
            element: <RequireAccess rule={{ staff: 'integrations:edit' }} />,
            children: [
              // Was its own page; now the GoHighLevel section of the venue's
              // settings, which is theirs too. Kept so old links land there.
              { path: 'venues/:venueId/gohighlevel', element: <VenueGoHighLevelRedirect /> },
              // Where HoneyBook's sign-in sends the person back: a venue's own
              // staff who set up integrations connect its HoneyBook.
              { path: 'honeybook/return', element: <HoneyBookReturnPage /> },
            ],
          },
          // Temporary Social Tables migration console — remove after cutover. An
          // import fills any of the company's venues.
          {
            element: <RequireAccess rule={{ staff: 'imports:edit', companyWide: true }} />,
            children: [{ path: 'admin/prism-import', element: <PrismImportPage /> }],
          },
          // What each kind of staff member can do, and billing: an organization admin's, whatever any role allows.
          {
            element: <RequireAccess rule={{ organizationAdmin: true }} />,
            children: [
              { path: 'roles', element: <RolesPage /> },
              { path: 'roles/new', element: <RoleEditorPage /> },
              { path: 'roles/:roleId', element: <RoleEditorPage /> },
              { path: 'billing', element: <BillingPage /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]

/**
 * What shows while a page's code arrives on the first page of a visit: a link
 * straight to a floor plan waits for the editor, which loads on its own. The
 * same mark index.html shows before the app itself has arrived.
 */
function LoadingPage() {
  return (
    <div role="status" className="flex min-h-svh flex-col items-center justify-center gap-3 bg-page text-sm text-ink-muted">
      <img src="/icon.svg" alt="" width={40} height={40} className="motion-safe:animate-pulse" />
      <p>Loading {PRODUCT_NAME}…</p>
    </div>
  )
}

/**
 * A pathless root route carries `errorElement` so a render error anywhere in the
 * tree bubbles to one place that reports it (see RouteErrorBoundary) instead of
 * hitting react-router's built-in error screen.
 */
export const router = createBrowserRouter([
  { element: <SiteHandoff />, errorElement: <RouteErrorBoundary />, hydrateFallbackElement: <LoadingPage />, children: appRoutes },
])
